import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { describeConfigError, validateCockpitConfig } from '../../utils/cockpitConfigValidator';
import CockpitBasicInfoTab from './CockpitBasicInfoTab';
import CockpitConfigTab from './CockpitConfigTab';

// Function to assign groups based on first word of tab names
const assignGroups = (tabs) => {
  // Count occurrences of first words
  const firstWordCount = {};

  tabs.forEach(tab => {
    const firstWord = tab.name.split(' ')[0];
    firstWordCount[firstWord] = (firstWordCount[firstWord] || 0) + 1;
  });

  // Assign group if first word appears more than once
  return tabs.map(tab => {
    const firstWord = tab.name.split(' ')[0];

    // Ensure consistent order of properties
    return {
      ...tab,
      name: tab.name,
      type: tab.type,
      slug: tab.slug,
      admin_only: tab.admin_only || false,
      group: tab.group !== null && tab.group !== undefined ? tab.group : (firstWordCount[firstWord] >= 2 ? firstWord : null),
      properties: tab.properties
    };
  });
};

const CockpitModal = ({ isOpen, onClose, cockpit, fetchWithAuth, onSave }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    config: {
      tabs: []
    },
    is_public: false
  });
  const [configError, setConfigError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when cockpit changes
  useEffect(() => {
    if (cockpit) {
      const config = cockpit.config || { tabs: [] };
      setFormData({
        name: cockpit.name || '',
        slug: cockpit.slug || '',
        description: cockpit.description || '',
        config,
        is_public: cockpit.is_public || false
      });
      // Une config enregistrée peut enfreindre les règles : le backend refuserait de
      // l'enregistrer, autant le signaler dès l'ouverture (sur une copie, le validateur
      // complète les slugs manquants)
      const validation = validateCockpitConfig(JSON.parse(JSON.stringify(config)));
      setConfigError(validation.valid ? '' : validation.error);
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        config: { tabs: [] },
        is_public: false
      });
      setConfigError('');
    }
  }, [cockpit]);

  // Rouvrir le même cockpit ne relance pas l'effet ci-dessus : l'échec d'un essai
  // précédent ne doit pas rester affiché
  useEffect(() => {
    if (isOpen) setSaveError('');
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleConfigChange = (newConfig) => {
    setFormData(prev => ({
      ...prev,
      config: newConfig
    }));
  };

  const generateSlug = () => {
    const slug = formData.name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

    setFormData(prev => ({
      ...prev,
      slug
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (configError) {
      setActiveTab('config');
      return;
    }

    try {
      setIsSubmitting(true);
      setSaveError('');

      // Recalculate groups before saving to ensure consistency
      const dataToSave = { ...formData };

      if (dataToSave.config && dataToSave.config.tabs && dataToSave.config.tabs.length > 0) {
        dataToSave.config = {
          ...dataToSave.config,
          tabs: assignGroups(dataToSave.config.tabs)
        };
      }

      const url = cockpit?.id
        ? `${API_BASE_URL}/api/admin/cockpits/${cockpit.id}`
        : `${API_BASE_URL}/api/admin/cockpits`;

      const method = cockpit?.id ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });

      if (!response.ok) {
        // Le backend explique son refus dans { message } (une règle de config, par exemple)
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `L'enregistrement a échoué (erreur ${response.status})`);
      }

      const savedCockpit = await response.json();
      onSave(savedCockpit);
      onClose();
    } catch (error) {
      console.error('Error saving cockpit:', error);
      setSaveError(describeConfigError(error.message, formData.config?.tabs));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white text-left">
            {cockpit?.id ? 'Modifier le cockpit' : 'Ajouter un cockpit'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('basic')}
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                activeTab === 'basic'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Informations de base
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                activeTab === 'config'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Configuration
              {configError && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                  !
                </span>
              )}
            </button>
          </nav>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-12rem)]">
          <form onSubmit={handleSubmit} className="text-left">
            {activeTab === 'basic' ? (
              <CockpitBasicInfoTab
                formData={formData}
                handleInputChange={handleInputChange}
                handleCheckboxChange={handleCheckboxChange}
                generateSlug={generateSlug}
              />
            ) : (
              <CockpitConfigTab
                config={formData.config}
                onConfigChange={handleConfigChange}
                setConfigError={setConfigError}
                configError={configError}
              />
            )}
          </form>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end">
          {(saveError || (configError && activeTab === 'basic')) && (
            <p role="alert" className="mr-auto pr-4 text-sm text-red-600 dark:text-red-400 text-left">
              {saveError || "La configuration contient une erreur à corriger : voir l'onglet Configuration."}
            </p>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 mr-2"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || configError}
            className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${(isSubmitting || configError) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CockpitModal;